import React, { useRef, useState } from 'react';
import {
  Grid,
  TextInput,
  Select,
  Radio,
  Group,
  Textarea,
  MultiSelect,
  Box,
  Text,
  Badge,
  ActionIcon,
  Paper,
  Title,
  Stack,
  Divider,
  ThemeIcon,
  Flex,
  Button,
  ScrollArea,
  Alert,
  Avatar,
  Tooltip,
  Modal,
  LoadingOverlay
} from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { IconUnlink, IconUser, IconCalendar, IconSettings, IconFileText, IconLink, IconUsers, IconSchool, IconX, IconInfoCircle, IconPlus, IconTrash, IconCheck, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { IconChalkboard } from '@tabler/icons-react';
import { Batch, BatchStatus, MouStatus, ScheduleStatus, Student, Instructor } from '../types/batch.types';
import { Color } from 'antd/es/color-picker';
import { size } from 'lodash';
import { useMediaQuery } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { IconVideoPlus } from '@tabler/icons-react';
import { ApiService } from '../../api/api-service';
import { StudentForm } from './StudentForm';
import { InstructorForm } from './InstructorForm';

interface BatchFormProps {
  formData: Partial<Batch>;
  onChange: (field: keyof Batch, value: any) => void;
  editMode?: boolean;
  batchNumbers?: string[];
  currentBatchNo?: string;
  studentData?: {
    student_id: string;
    name: string;
    email: string;
    mobile: string;
    enrollDate: string;
  };
  onStudentChange?: (field: string, value: string) => void;
  onSubmitStudent?: () => void;
  studentLoading?: boolean;
   handleMergePreview?: (batchNos: string[]) => void; 
  getBatchByBatchNo?: (batchNo: string) => Partial<Batch> | null;
  handleUnmergeBatches?: (batchNos: string[]) => Promise<void>;
  adminDropdown?: { label: string; value: string }[];
  instructorsDropdown?: { label: string; value: string }[];
  instructorsRef?: React.RefObject<any[]>;
  studentsDropdown?: { label: string; value: string; name: string }[];
  studentsRef?: React.RefObject<any[]>;
  onStudentFormChange?: (index: number, field: keyof Student, value: any) => void;
  onStudentEmailChange?: (index: number, email: string | null) => void;
  onInstructorFormChange?: (index: number, field: keyof Instructor, value: any) => void;
  onInstructorEmailChange?: (index: number, email: string | null) => void;
  onAddStudent?: () => void;
  onRemoveStudent?: (index: number) => void;
  onAddInstructor?: () => void;
  onRemoveInstructor?: (index: number) => void;
  onCreateStudent?: () => Promise<boolean>;
  onOpenCreateStudentModal?: () => void;
  isEditBatch?: boolean;
  isSuperAdmin?: boolean;
}

const BatchForm: React.FC<BatchFormProps> = ({
  formData,
  onChange,
  editMode = false,
  batchNumbers = [],
  currentBatchNo,
  studentData,
  onStudentChange,
  onSubmitStudent,
  studentLoading,
  handleMergePreview,
  handleUnmergeBatches,
  getBatchByBatchNo = () => null, // default fallback
  adminDropdown = [], 
  instructorsDropdown = [],
  instructorsRef,
  studentsDropdown = [],
  studentsRef,
  onStudentFormChange,
  onStudentEmailChange,
  onInstructorFormChange,
  onInstructorEmailChange,
  onAddStudent,
  onRemoveStudent,
  onAddInstructor,
  onRemoveInstructor,
  onCreateStudent,
  onOpenCreateStudentModal,
  isEditBatch = false,
  isSuperAdmin = false,
}) => {
  // Detect mobile screen
  const isMobile = useMediaQuery('(max-width: 768px)');
  
  // Auto-create a Google Meet link (+ spaceId) using the selected GM account
  const [creatingMeeting, setCreatingMeeting] = useState(false);
  const handleCreateMeetingLink = async () => {
    if (!formData.gmAccountName) return;
    setCreatingMeeting(true);
    try {
      const currentInstructor = (formData.instructor || [])[currentInstructorIndex];
      const { data } = await ApiService.getInstance().post('/batches/create-meeting-link', {
        gmAccountName: formData.gmAccountName,
        batch_no: formData.batch_no,
        courseName: formData.courseName,
        trainerEmail: formData.trainerEmail || currentInstructor?.email,
        trainerName: formData.trainerName || currentInstructor?.name,
        startDate: formData.startDate,
        batchTiming: currentInstructor?.batchTiming,
        scheduleStatus: formData.scheduleStatus,
      });
      onChange('meetingLink', data.meetingLink);
      onChange('meetingId', data.meetingId);
      onChange('spaceId', data.spaceId || '');
      onChange('calendarEditLink', data.calendarEditLink || '');
      // Config/co-host problems are logged only; the admin just sees success
      if (data.warnings?.length) console.warn('[create-meeting-link] warnings:', data.warnings);
      showNotification({
        color: 'green',
        title: 'Meeting link created',
        message: data.meetingLink,
      });
    } catch (err: any) {
      showNotification({
        color: 'red',
        title: 'Could not create meeting link',
        message: err?.response?.data?.details || err?.response?.data?.error || err.message,
      });
    } finally {
      setCreatingMeeting(false);
    }
  };

  // Create refs for each section
  const basicInfoRef = useRef<HTMLDivElement>(null);
  const trainerInfoRef = useRef<HTMLDivElement>(null);
  const statusConfigRef = useRef<HTMLDivElement>(null);
  const mergeBatchesRef = useRef<HTMLDivElement>(null);
  const commentsRef = useRef<HTMLDivElement>(null);
  const studentsSectionRef = useRef<HTMLDivElement>(null);
  const instructorsSectionRef = useRef<HTMLDivElement>(null);
  // const instructorsRef = useRef<HTMLDivElement>(null);
  
  // Create ref for ScrollArea viewport to control horizontal scroll
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  
  // Function to scroll navigation left/right
  const scrollNavigation = (direction: 'left' | 'right') => {
    if (scrollViewportRef.current) {
      const scrollAmount = 200; // pixels to scroll
      const currentScroll = scrollViewportRef.current.scrollLeft;
      const targetScroll = direction === 'left' 
        ? currentScroll - scrollAmount 
        : currentScroll + scrollAmount;
      
      scrollViewportRef.current.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }
  };

  // Navigation sections configuration
  const navigationSections = [
    {
      id: 'basic-info',
      label: 'Basic Info.',
      ref: basicInfoRef,
      icon: <IconFileText size={16} />,
      color: 'blue',
      size: 'xs'

    },
    // {
    //   id: 'trainer-info',
    //   label: 'Trainer Info.',
    //   ref: trainerInfoRef,
    //   icon: <IconUser size={16} />,
    //   color: 'green',
    //   fontSizes: '5px'
    // },
    {
      id: 'status-config',
      label: 'Status',
      ref: statusConfigRef,
      icon: <IconSettings size={16} />,
      color: 'purple'
    },
    {
      id: 'merge-batches',
      label: 'Merge',
      ref: mergeBatchesRef,
      icon: <IconLink size={16} />,
      color: 'orange'
    },
    {
      id: 'comments',
      label: 'Comments',
      ref: commentsRef,
      icon: <IconFileText size={16} />,
      color: 'teal'
    },
    {
      id: 'students',
      label: 'Students',
      ref: studentsSectionRef,
      icon: <IconUsers size={16} />,
      color: 'indigo'
    },
    {
      id: 'instructors',
      label: 'Instructors',
      ref: instructorsSectionRef,
      icon: <IconSchool size={16} />,
      color: 'pink'
    }
  ];

  // Scroll to section function
  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const getFilteredBatchOptions = (currentBatchNo: string) => {
    return batchNumbers.filter(batchNo => batchNo !== currentBatchNo);
  };

  // Batch Timing lives on the instructor array (per-instructor stint), but is
  // surfaced here as one Basic Info field. Prefer the entry matching the
  // CURRENT trainer (trainerEmail) so batches with trainer turnover don't
  // show/edit a former instructor's timing; fall back to the first entry.
  // -1 means no instructor exists yet — the field stays disabled rather than
  // silently creating a blank ghost instructor just to hold a time value.
  const currentInstructorIndex = (() => {
    const list = formData.instructor || [];
    if (list.length === 0) return -1;
    if (formData.trainerEmail) {
      const idx = list.findIndex(
        (i) => (i.email || '').toLowerCase().trim() === formData.trainerEmail!.toLowerCase().trim(),
      );
      if (idx !== -1) return idx;
    }
    return 0;
  })();

  const SectionHeader = ({ icon, title, gradient }: { icon: React.ReactNode, title: string, gradient: string }) => (
    <Flex align="center" mb={isMobile ? "xs" : "sm"} className="py-2">
      <ThemeIcon
        size={isMobile ? "sm" : "md"}
        radius="md"
        variant="light"
        color="blue"
        className="shadow-sm mr-2"
      >
        {icon}
      </ThemeIcon>
      <Title order={5} className="text-gray-800 font-semibold text-sm">
        {title}
      </Title>
    </Flex>
  );
  return (
    <Stack gap={isMobile ? "md" : "xl"} p={isMobile ? "xs" : "md"}>
      {/* Navigation Section */}
      {/* Navigation Section - Positioned at top of modal */}
      <Paper
        shadow="xl"
        radius={isMobile ? "lg" : "xl"}
        p={isMobile ? "sm" : "lg"}
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(243,244,246,0.98) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.4)',
          borderBottom: '2px solid rgba(229,231,235,0.6)',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          margin: isMobile ? '-0.5rem -0.5rem 1rem -0.5rem' : '-1rem -1rem 1.5rem -1rem',
          borderRadius: isMobile ? '0.5rem 0.5rem 0 0' : '0.75rem 0.75rem 0 0',
        }}
      >
        {/* <Title
          order={2}
          c="gray.8"
          mb="lg"
          ta="center"
          style={{
            fontSize: '1.5rem',
            fontWeight: 600,
            letterSpacing: '-0.025em'
          }}
        >
          {editMode ? 'Edit Batch Details' : 'Batch Overview'}
        </Title> */}

        {/* <Text
          size="xs"
          fw={500}
          c="gray.6"
          mb="md"
          ta="center"
          tt="uppercase"
          style={{ letterSpacing: '0.1em' }}
        >
          Quick Navigation
        </Text> */}

        {/* Quick Navigation Title with Icon */}
        {/* <Group justify="center" mb={isMobile ? "xs" : "sm"} gap="xs">
          <Text
            size={isMobile ? "xs" : "sm"}
            fw={600}
            c="gray.7"
            ta="center"
            tt="uppercase"
            style={{ letterSpacing: '0.05em' }}
          >
            📍 Quick Navigation - Click to Jump
          </Text>
        </Group> */}

        {/* Navigation with Arrow Buttons */}
        <Group gap="xs" align="center" wrap="nowrap">
          {/* Left Arrow Button */}
          <ActionIcon
            variant="subtle"
            color="blue"
            size={isMobile ? "lg" : "xl"}
            radius="xl"
            onClick={() => scrollNavigation('left')}
            style={{
              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.15)',
              transition: 'all 0.2s ease',
              background: 'linear-gradient(135deg, rgba(219, 234, 254, 0.5) 0%, rgba(191, 219, 254, 0.3) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
            }}
            className="hover:scale-110"
          >
            <IconChevronLeft size={isMobile ? 18 : 20} stroke={2.5} />
          </ActionIcon>

          <ScrollArea
            viewportRef={scrollViewportRef}
            type="never"
            style={{
              paddingBottom: '4px',
              flex: 1
            }}
          >
            <Flex
              gap={isMobile ? "xs" : "sm"}
              // wrap={isMobile ? "wrap" : "nowrap"}
              justify="center"
              style={{
                minWidth: isMobile ? 'auto' : 'max-content',
                paddingBottom: '2px'
              }}
            >
            {navigationSections.map((section) => (
              <Button
                key={section.id}
                variant="light"
                color={section.color}
                size={isMobile ? "xs" : "sm"}
                radius="xl"
                leftSection={isMobile ? null : section.icon}
                onClick={() => scrollToSection(section.ref)}
                styles={{
                  inner: {
                    justifyContent: 'flex-start',
                    gap: '0.5rem'
                  },
                  root: {
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    minWidth: 'max-content',
                    fontWeight: 500,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    height: isMobile ? '30px' : '36px',
                    paddingLeft: isMobile ? '0.75rem' : '1rem',
                    paddingRight: isMobile ? '0.75rem' : '1rem',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    '&:hover': {
                      transform: 'translateY(-2px) scale(1.02)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                      filter: 'brightness(1.05)',
                    },
                    '&:active': {
                      transform: 'translateY(-1px) scale(1.01)',
                    }
                  }
                }}
              >
                {isMobile && section.icon} {section.label}
              </Button>
            ))}
          </Flex>
        </ScrollArea>

        {/* Right Arrow Button */}
        <ActionIcon
          variant="subtle"
          color="blue"
          size={isMobile ? "lg" : "xl"}
          radius="xl"
          onClick={() => scrollNavigation('right')}
          style={{
            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.15)',
            transition: 'all 0.2s ease',
            background: 'linear-gradient(135deg, rgba(219, 234, 254, 0.5) 0%, rgba(191, 219, 254, 0.3) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
          }}
          className="hover:scale-110"
        >
          <IconChevronRight size={isMobile ? 18 : 20} stroke={2.5} />
        </ActionIcon>
      </Group>

      {/* Helper Text with Animation */}
      {/* <Group justify="center" mt="xs">
        <Text
          size="xs"
          c="dimmed"
          ta="center"
          style={{
            fontSize: isMobile ? '10px' : '11px',
            fontStyle: 'italic',
            animation: 'fadeInOut 3s ease-in-out infinite',
          }}
        >
          💡 Tip: Click any section above to scroll instantly
        </Text>
      </Group> */}

      {/* Add CSS animation keyframes */}
      <style>{`
        @keyframes fadeInOut {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
      `}</style>
    </Paper>
      {/* Basic Information Section */}
      <Paper
        ref={basicInfoRef}
        shadow="sm"
        radius="md"
        p={isMobile ? "xs" : "sm"}
        className="bg-white border border-gray-200"
        style={{
          scrollMarginTop: isMobile ? '80px' : '100px',
        }}
      >
        <SectionHeader
          icon={<IconFileText size={16} />}
          title="Basic Information"
          gradient="blue indigo"
        />

        <Grid gutter={isMobile ? "xs" : "sm"}>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Batch Number"
              placeholder="Enter batch number"
              value={formData.batch_no || ''}
              onChange={(e) => onChange('batch_no', e.target.value.trim())}
              onBlur={(e) => onChange('batch_no', e.target.value.trim())}
              readOnly={!editMode}
              size="xs"
              styles={{
                label: { fontWeight: 600, color: '#374151', marginBottom: 4, fontSize: '0.75rem' },
                input: {
                  borderRadius: 6,
                  border: '1px solid #e5e7eb',
                  padding: '6px 10px',
                  fontSize: '0.75rem',
                  height: '32px',
                  '&:focus': {
                    borderColor: '#3b82f6',
                    boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.1)',
                  },
                  '&:hover': {
                    borderColor: '#9ca3af',
                  },
                },
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Course Name"
              placeholder="Enter course name"
              value={formData.courseName || ''}
              onChange={(e) => onChange('courseName', e.target.value)}
              readOnly={!editMode}
              styles={{
                label: { fontWeight: 600, color: '#374151', marginBottom: isMobile ? 6 : 8, fontSize: isMobile ? '0.875rem' : '1rem' },
                input: {
                  borderRadius: isMobile ? 8 : 12,
                  border: '2px solid #e5e7eb',
                  padding: isMobile ? '10px 12px' : '12px 16px',
                  fontSize: isMobile ? 13 : 14,
                  '&:focus': {
                    borderColor: '#3b82f6',
                    boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.1)',
                  },
                  '&:hover': {
                    borderColor: '#9ca3af',
                  },
                },
              }}
            />
          </Grid.Col>
<Grid.Col span={{ base: 12, sm: 6 }}>
  <TimeInput
    label="Batch Timing"
    placeholder={currentInstructorIndex === -1 ? 'Add an instructor first' : 'Select batch timing'}
    value={currentInstructorIndex === -1 ? '' : formData.instructor?.[currentInstructorIndex]?.batchTiming || ''}
    onChange={(event) =>
      currentInstructorIndex !== -1 &&
      onInstructorFormChange?.(currentInstructorIndex, 'batchTiming', event.currentTarget.value)
    }
    readOnly={!editMode}
    disabled={currentInstructorIndex === -1}
    withSeconds={false}
    size="xs"
    description={currentInstructorIndex === -1 ? 'Add an instructor below to set the batch timing' : undefined}
    styles={{
      label: { fontWeight: 600, color: '#374151', marginBottom: 4, fontSize: '0.75rem' },
      input: {
        borderRadius: 6,
        border: '1px solid #e5e7eb',
        padding: '6px 10px',
        fontSize: '0.75rem',
        height: '32px',
      },
    }}
  />
</Grid.Col>
<Grid.Col span={{ base: 12, sm: 6 }}>
  <Select
    label="Admin"
    placeholder="Select admin"
    data={adminDropdown}
    value={formData.admin || ''}
    onChange={(value) => onChange('admin', value)}
    readOnly={!editMode}
    searchable
    styles={{
      label: { fontWeight: 600, color: '#374151', marginBottom: isMobile ? 6 : 8, fontSize: isMobile ? '0.875rem' : '1rem' },
      input: {
        borderRadius: isMobile ? 8 : 12,
        border: '2px solid #e5e7eb',
        padding: isMobile ? '10px 12px' : '12px 16px',
        fontSize: isMobile ? 13 : 14,
        '&:focus': {
          borderColor: '#3b82f6',
          boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.1)',
        },
        '&:hover': {
          borderColor: '#9ca3af',
        },
      },
    }}
  />
</Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
  <Select
    label="Current Trainer Email"
    placeholder="Select trainer email"
    data={instructorsDropdown || []}
    value={formData.trainerEmail || ''}
    onChange={(email) => {
      onChange('trainerEmail', email);
      // Auto-populate trainer name
      if (email && instructorsRef?.current) {
        const selectedInstructor = instructorsRef.current.find(
          (instructor: any) => instructor.email === email
        );
        if (selectedInstructor) {
          onChange('trainerName', selectedInstructor.name);
        }
      } else {
        onChange('trainerName', '');
      }
    }}
    readOnly={!editMode}
    searchable
    styles={{
      label: { fontWeight: 600, color: '#374151', marginBottom: isMobile ? 6 : 8, fontSize: isMobile ? '0.875rem' : '1rem' },
      input: {
        borderRadius: isMobile ? 8 : 12,
        border: '2px solid #e5e7eb',
        padding: isMobile ? '10px 12px' : '12px 16px',
        fontSize: isMobile ? 13 : 14,
        '&:focus': {
          borderColor: '#3b82f6',
          boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.1)',
        },
        '&:hover': {
          borderColor: '#9ca3af',
        },
      },
    }}
  />
</Grid.Col>

<Grid.Col span={{ base: 12, sm: 6 }}>
  <TextInput
    label="Current Trainer Name"
    placeholder="Trainer name (auto-filled)"
    value={formData.trainerName || ''}
    onChange={(e) => onChange('trainerName', e.target.value)}
    readOnly={true} // Always readonly as it's auto-populated
    styles={{
      label: { fontWeight: 600, color: '#374151', marginBottom: isMobile ? 6 : 8, fontSize: isMobile ? '0.875rem' : '1rem' },
      input: {
        borderRadius: isMobile ? 8 : 12,
        border: '2px solid #e5e7eb',
        padding: isMobile ? '10px 12px' : '12px 16px',
        fontSize: isMobile ? 13 : 14,
        backgroundColor: '#f9fafb',
      },
    }}
  />
</Grid.Col>
{/* — add after the "Current Trainer Name" Grid.Col — */}

<Grid.Col span={{ base: 12, sm: 6 }}>
  <TextInput
    label="Google Meet Link"
    placeholder="https://meet.google.com/xxx-xxxx-xxx"
    value={formData.meetingLink || ''}
    onChange={(e) => {
      const link = e.target.value;
      onChange('meetingLink', link);
      // A hand-edited link no longer matches the stored Meet space
      onChange('spaceId', '');
      onChange('calendarEditLink', '');
      // Auto-extract meeting ID from URL
      const match = link.match(/meet\.google\.com\/([a-z0-9-]+)/i);
      onChange('meetingId', match ? match[1] : '');
    }}
    readOnly={!editMode}
    size="xs"
    styles={{
      label: { fontWeight: 600, color: '#374151', marginBottom: 4, fontSize: '0.75rem' },
      input: {
        borderRadius: 6,
        border: '1px solid #e5e7eb',
        padding: '6px 10px',
        fontSize: '0.75rem',
        height: '32px',
      },
    }}
  />
</Grid.Col>

<Grid.Col span={{ base: 12, sm: 6 }}>
  <TextInput
    label="Meeting ID"
    placeholder="Auto-extracted from link"
    value={formData.meetingId || ''}
    readOnly={true}
    size="xs"
    styles={{
      label: { fontWeight: 600, color: '#374151', marginBottom: 4, fontSize: '0.75rem' },
      input: {
        borderRadius: 6,
        border: '1px solid #e5e7eb',
        padding: '6px 10px',
        fontSize: '0.75rem',
        height: '32px',
        backgroundColor: '#f9fafb',
      },
    }}
  />
</Grid.Col>

<Grid.Col span={{ base: 12, sm: 6 }}>
  <TextInput
    label="Access Code"
    placeholder="Auto-generated when the batch is created"
    value={formData.accessCode || ''}
    readOnly={true}
    size="xs"
    styles={{
      label: { fontWeight: 600, color: '#374151', marginBottom: 4, fontSize: '0.75rem' },
      input: {
        borderRadius: 6,
        border: '1px solid #e5e7eb',
        padding: '6px 10px',
        fontSize: '0.75rem',
        height: '32px',
        backgroundColor: '#f9fafb',
      },
    }}
  />
</Grid.Col>

<Grid.Col span={{ base: 12, sm: 6 }}>
  <Select
    label="G.M Account's Name"
    placeholder="Select GM Account"
    value={formData.gmAccountName || ''}
    onChange={(val) => onChange('gmAccountName', val || '')}
    data={[
      { value: 'Varun Kumar', label: 'Varun Kumar' },
      { value: 'Ravi Gupta', label: 'Ravi Gupta' },
      { value: 'Suman Thinkcloudly', label: 'Suman Thinkcloudly' },
      { value: 'Sai Lakshmi', label: 'Sai Lakshmi' },
      { value: 'Uma Shankar', label: 'Uma Shankar' },
    ]}
    readOnly={!editMode}
    clearable
    searchable
    size="xs"
    styles={{
      label: { fontWeight: 600, color: '#374151', marginBottom: 4, fontSize: '0.75rem' },
      input: {
        borderRadius: 6,
        border: '1px solid #e5e7eb',
        padding: '6px 10px',
        fontSize: '0.75rem',
        height: '32px',
      },
    }}
  />
</Grid.Col>

<Grid.Col span={{ base: 12, sm: 6 }} style={{ display: 'flex', alignItems: 'flex-end' }}>
  {editMode && (
    <Tooltip
      label={formData.gmAccountName ? 'Create a Google Meet link with this account' : 'Select a G.M account first'}
      withArrow
    >
      <Button
        size="xs"
        variant="light"
        leftSection={<IconVideoPlus size={14} />}
        loading={creatingMeeting}
        disabled={!formData.gmAccountName}
        onClick={handleCreateMeetingLink}
      >
        {formData.meetingLink ? 'Re-create Meeting Link' : 'Create Meeting Link'}
      </Button>
    </Tooltip>
  )}
</Grid.Col>

{formData.calendarEditLink && (() => {
  // The event lives on the GM account's calendar. The link carries authuser=<email>, so Google
  // opens it directly in that account when it is already signed in in this browser (no chooser).
  let gmEmail = '';
  try {
    gmEmail = new URL(formData.calendarEditLink!).searchParams.get('authuser') || '';
  } catch {
    /* link without authuser — plain link is used */
  }
  const href = formData.calendarEditLink!;
  return (
    <Grid.Col span={12}>
      <Button component="a" href={href} target="_blank" rel="noopener noreferrer" size="xs" variant="outline">
        Manage co-hosts in Google Calendar
      </Button>
      <Text size="xs" c="dimmed" mt={4}>
        {gmEmail ? <>e.g, <b>{gmEmail}</b> </> : null}
        Then: Meet settings (gear) → Guests → Add co-host → Save.
      </Text>
    </Grid.Col>
  );
})()}
        </Grid>
      </Paper>


      {/* Status and Configuration Section */}
      <Paper
        ref={statusConfigRef}
        shadow="md"
        radius={isMobile ? "md" : "lg"}
        p={isMobile ? "sm" : "md"}
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(252,231,243,0.9) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)',
          scrollMarginTop: isMobile ? '80px' : '100px',
        }}
      >
        <SectionHeader
          icon={<IconSettings size={isMobile ? 18 : 20} />}
          title="Status & Configuration"
          gradient="purple pink"
        />

        <Grid gutter={isMobile ? "md" : "lg"} mb={isMobile ? "md" : "xl"}>
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <Select
              label="Batch Status"
              placeholder="Select batch status"
              data={["Completed", "Hold", "On-going", "Not Started", "Absconded", "Discarded"]}
              value={formData.batchStatus || ''}
              onChange={(value) => onChange('batchStatus', value)}
              readOnly={!editMode}
              styles={{
                label: { fontWeight: 600, color: '#374151', marginBottom: isMobile ? 6 : 8, fontSize: isMobile ? '0.875rem' : '1rem' },
                input: {
                  borderRadius: isMobile ? 8 : 12,
                  border: '2px solid #e5e7eb',
                  padding: isMobile ? '10px 12px' : '12px 16px',
                  fontSize: isMobile ? 13 : 14,
                  '&:focus': {
                    borderColor: '#a855f7',
                    boxShadow: '0 0 0 4px rgba(168, 85, 247, 0.1)',
                  },
                  '&:hover': {
                    borderColor: '#9ca3af',
                  },
                },
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <TextInput
              label="No. of Sessions"
              type="text"
              inputMode="numeric"
              placeholder="Enter number of sessions"
              value={formData.numSessions || ''}
              onChange={(e) => {
                const value = e.target.value;
                // Allow only numbers
                if (value === '' || /^\d+$/.test(value)) {
                  onChange('numSessions', value === '' ? '' : Number(value));
                }
              }}
              readOnly={!editMode}
              styles={{
                label: { fontWeight: 600, color: '#374151', marginBottom: isMobile ? 6 : 8, fontSize: isMobile ? '0.875rem' : '1rem' },
                input: {
                  borderRadius: isMobile ? 8 : 12,
                  border: '2px solid #e5e7eb',
                  padding: isMobile ? '10px 12px' : '12px 16px',
                  fontSize: isMobile ? 13 : 14,
                  '&:focus': {
                    borderColor: '#a855f7',
                    boxShadow: '0 0 0 4px rgba(168, 85, 247, 0.1)',
                  },
                  '&:hover': {
                    borderColor: '#9ca3af',
                  },
                },
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <TextInput
              label="Start Date"
              type="date"
              value={formData.startDate || ''}
              onChange={(e) => onChange('startDate', e.target.value)}
              readOnly={!editMode}
              styles={{
                label: { fontWeight: 600, color: '#374151', marginBottom: isMobile ? 6 : 8, fontSize: isMobile ? '0.875rem' : '1rem' },
                input: {
                  borderRadius: isMobile ? 8 : 12,
                  border: '2px solid #e5e7eb',
                  padding: isMobile ? '10px 12px' : '12px 16px',
                  fontSize: isMobile ? 13 : 14,
                  '&:focus': {
                    borderColor: '#a855f7',
                    boxShadow: '0 0 0 4px rgba(168, 85, 247, 0.1)',
                  },
                  '&:hover': {
                    borderColor: '#9ca3af',
                  },
                },
              }}
            />
          </Grid.Col>
        </Grid>

        {/* MOU Status */}
        <Box mb={isMobile ? "md" : "xl"}>
          <Text fw={600} c="gray.7" mb="md" size={isMobile ? "sm" : "md"}>MOU Status</Text>
          <Radio.Group
            value={formData.MouStatus || ''}
            onChange={(value) => onChange('MouStatus', value)}
          >
            <Group gap={isMobile ? "md" : "xl"}>
              <Radio
                value="Signed"
                label="Signed"
                styles={{
                  label: { fontSize: isMobile ? 13 : 14, fontWeight: 500 },
                  radio: { '&:checked': { backgroundColor: '#a855f7', borderColor: '#a855f7' } }
                }}
              />
              <Radio
                value="Sent"
                label="Sent"
                styles={{
                  label: { fontSize: isMobile ? 13 : 14, fontWeight: 500 },
                  radio: { '&:checked': { backgroundColor: '#a855f7', borderColor: '#a855f7' } }
                }}
              />
              <Radio
                value="Not Sent"
                label="Not Sent"
                styles={{
                  label: { fontSize: isMobile ? 13 : 14, fontWeight: 500 },
                  radio: { '&:checked': { backgroundColor: '#a855f7', borderColor: '#a855f7' } }
                }}
              />
              <Radio
                value="Expired"
                label="Expired"
                styles={{
                  label: { fontSize: isMobile ? 13 : 14, fontWeight: 500 },
                  radio: { '&:checked': { backgroundColor: '#a855f7', borderColor: '#a855f7' } }
                }}
              />
            </Group>
          </Radio.Group>
        </Box>

        {/* Orientation & Bridge Course */}
        <Grid gutter={isMobile ? "sm" : "lg"} mb={isMobile ? "md" : "xl"}>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Text fw={600} c="gray.7" mb="md" ta="left" size={isMobile ? "sm" : "md"}>Orientation</Text>
            <Radio.Group
              value={formData.orientation || ''}
              onChange={(value) => onChange('orientation', value)}
            >
              <Group gap={isMobile ? "md" : "xl"}>
                <Radio
                  value="Yes"
                  label="Yes"
                  styles={{
                    label: { fontSize: isMobile ? 13 : 14, fontWeight: 500 },
                    radio: { '&:checked': { backgroundColor: '#a855f7', borderColor: '#a855f7' } }
                  }}
                />
                <Radio
                  value="No"
                  label="No"
                  styles={{
                    label: { fontSize: isMobile ? 13 : 14, fontWeight: 500 },
                    radio: { '&:checked': { backgroundColor: '#a855f7', borderColor: '#a855f7' } }
                  }}
                />
              </Group>
            </Radio.Group>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Text fw={600} c="gray.7" mb="md" ta="left" size={isMobile ? "sm" : "md"}>Bridge Course</Text>
            <Radio.Group
              value={formData.bridgeCourse || ''}
              onChange={(value) => onChange('bridgeCourse', value)}
            >
              <Group gap={isMobile ? "md" : "xl"}>
                <Radio
                  value="Yes"
                  label="Yes"
                  styles={{
                    label: { fontSize: isMobile ? 13 : 14, fontWeight: 500 },
                    radio: { '&:checked': { backgroundColor: '#a855f7', borderColor: '#a855f7' } }
                  }}
                />
                <Radio
                  value="No"
                  label="No"
                  styles={{
                    label: { fontSize: isMobile ? 13 : 14, fontWeight: 500 },
                    radio: { '&:checked': { backgroundColor: '#a855f7', borderColor: '#a855f7' } }
                  }}
                />
              </Group>
            </Radio.Group>
          </Grid.Col>
        </Grid>

        {/* Schedule Status */}
        <Box>
          <Text fw={600} c="gray.7" mb="md" size={isMobile ? "sm" : "md"}>Schedule Status</Text>
          <Radio.Group
            value={formData.scheduleStatus || ''}
            onChange={(value) => {
              onChange('scheduleStatus', value);
              if (value !== 'Flexible') {
                onChange('scheduleDetails', '');
              }
            }}
          >
            <Group gap={isMobile ? "md" : "xl"} mb="md">
              <Radio
                value="Weekdays"
                label="Weekdays"
                styles={{
                  label: { fontSize: isMobile ? 13 : 14, fontWeight: 500 },
                  radio: { '&:checked': { backgroundColor: '#a855f7', borderColor: '#a855f7' } }
                }}
              />
              <Radio
                value="Weekends"
                label="Weekends"
                styles={{
                  label: { fontSize: isMobile ? 13 : 14, fontWeight: 500 },
                  radio: { '&:checked': { backgroundColor: '#a855f7', borderColor: '#a855f7' } }
                }}
              />
              <Radio
                value="Flexible"
                label="Flexible"
                styles={{
                  label: { fontSize: isMobile ? 13 : 14, fontWeight: 500 },
                  radio: { '&:checked': { backgroundColor: '#a855f7', borderColor: '#a855f7' } }
                }}
              />
              <Radio
                value="All Days"
                label="All Days"
                styles={{
                  label: { fontSize: isMobile ? 13 : 14, fontWeight: 500 },
                  radio: { '&:checked': { backgroundColor: '#a855f7', borderColor: '#a855f7' } }
                }}
              />
            </Group>
          </Radio.Group>

          {formData.scheduleStatus === 'Flexible' && (
            <Textarea
              label="Schedule Details"
              placeholder="Enter schedule details..."
              value={formData.scheduleDetails || ''}
              onChange={(e) => onChange('scheduleDetails', e.target.value)}
              readOnly={!editMode}
              rows={3}
              styles={{
                label: { fontWeight: 600, color: '#374151', marginBottom: isMobile ? 6 : 8, fontSize: isMobile ? '0.875rem' : '1rem' },
                input: {
                  borderRadius: isMobile ? 8 : 12,
                  border: '2px solid #e5e7eb',
                  padding: isMobile ? '10px 12px' : '12px 16px',
                  fontSize: isMobile ? 13 : 14,
                  '&:focus': {
                    borderColor: '#a855f7',
                    boxShadow: '0 0 0 4px rgba(168, 85, 247, 0.1)',
                  },
                  '&:hover': {
                    borderColor: '#9ca3af',
                  },
                },
              }}
            />
          )}
        </Box>
      </Paper>

      {/* Merge Batches Section */}
      <Paper
        ref={mergeBatchesRef}
        shadow="md"
        radius={isMobile ? "md" : "lg"}
        p={isMobile ? "sm" : "md"}
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,237,213,0.9) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)',
          scrollMarginTop: isMobile ? '80px' : '100px',
        }}
      >
        <Group justify="apart" align="center" mb="md" wrap={isMobile ? "wrap" : "nowrap"}>
          <Group>
            <IconLink size={isMobile ? 18 : 20} color="#f97316" />
            <Text
              size={isMobile ? "md" : "lg"}
              variant="gradient"
              gradient={{ from: 'orange', to: 'red' }}
            >
              Merge Batches
            </Text>
          </Group>
          {/* Add merge preview button */}
          {formData.linkedBatches && formData.linkedBatches.length > 0 && (
            <Button
              variant="outline"
              size="xs"
              color="blue"
              onClick={() =>
                handleMergePreview &&
                handleMergePreview(
                  [formData.batch_no, ...(formData.linkedBatches || [])].filter(
                    (b): b is string => typeof b === 'string' && b.length > 0
                  )
                )
              }
            >
              Preview Merge
            </Button>
          )}
        </Group>

        <Text size={isMobile ? "xs" : "sm"} color="dimmed" mb="xs">
          Select batches to merge. Students and instructors will be automatically merged during Merging.
        </Text>

        <MultiSelect
          label="Select Batches to Link"
          placeholder="Choose batches to merge with this batch"
          data={getFilteredBatchOptions(formData.batch_no || '')}
          value={formData.linkedBatches || []}
          onChange={(value) => onChange('linkedBatches', value)}
          searchable
          readOnly={!editMode}
          mb="md"
          styles={{
            label: { fontWeight: 600, color: '#374151', marginBottom: isMobile ? 6 : 8, fontSize: isMobile ? '0.875rem' : '1rem' },
            input: {
              borderRadius: isMobile ? 8 : 12,
              border: '2px solid #e5e7eb',
              padding: isMobile ? '10px 12px' : '12px 16px',
              fontSize: isMobile ? 13 : 14,
              '&:focus': {
                borderColor: '#f97316',
                boxShadow: '0 0 0 4px rgba(249, 115, 22, 0.1)',
              },
              '&:hover': {
                borderColor: '#9ca3af',
              },
            },
          }}
        />

        {/* Enhanced linked batches display */}
       {/* Enhanced linked batches display with unmerge option */}
{formData.linkedBatches && formData.linkedBatches.length > 0 && (
  <Paper withBorder p={isMobile ? "sm" : "md"} mb="md" bg="orange.0">
    <Group justify="apart" mb="xs" wrap={isMobile ? "wrap" : "nowrap"}>
      <Text size={isMobile ? "xs" : "sm"} color="orange.8">
        Linked Batches:
      </Text>
      <Group gap="xs">
        <Badge color="orange" variant="light" size="xs">
          {formData.linkedBatches.length} batch{formData.linkedBatches.length > 1 ? 'es' : ''}
        </Badge>
     {editMode && (
  <Button
    size="xs"
    variant="outline"
    color="red"
    leftSection={<IconUnlink size={12} />}
    onClick={() => {
      if (!formData.batch_no) {
        alert('Batch number is missing');
        return;
      }
      
      if (window.confirm('Are you sure you want to unmerge these batches? This will restore original data for each batch.')) {
        handleUnmergeBatches?.([formData.batch_no, ...(formData.linkedBatches || [])]);
      }
    }}
  >
    Unmerge All
  </Button>
)}
      </Group>
    </Group>
    <Group gap="xs" wrap="wrap">
      {formData.linkedBatches.map((batchNo) => {
        const linkedBatch = getBatchByBatchNo(batchNo);
        return (
          <Badge
            key={batchNo}
            color="orange"
            variant="filled"
            size={isMobile ? "sm" : "md"}
            rightSection={
              editMode ? (
                <ActionIcon
                  size="xs"
                  color="orange"
                  radius="xl"
                  variant="transparent"
                  onClick={() => onChange(
                    'linkedBatches',
                    (formData.linkedBatches || []).filter(b => b !== batchNo)
                  )}
                  style={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' } }}
                >
                  <IconX size={10} />
                </ActionIcon>
              ) : null
            }
          >
            {batchNo}
            {linkedBatch && (
              <Text span size="xs" ml={4} style={{ opacity: 0.8 }}>
                ({linkedBatch.students?.length || 0} students)
              </Text>
            )}
          </Badge>
        );
      })}
    </Group>
    <Text size="xs" color="dimmed" mt="xs">
      💡 When you save, students and instructors from all linked batches will be automatically merged.
    </Text>
  </Paper>
)}

        {/* Warning for existing linked batches */}
        {!editMode && formData.linkedBatches && formData.linkedBatches.length > 0 && (
          <Alert
            icon={<IconInfoCircle size={16} />}
            title="Merge Information"
            color="blue"
            variant="light"
            mb="md"
          >
            <Text size={isMobile ? "xs" : "sm"}>
              This batch is Merge with {formData.linkedBatches.length} other batch{formData.linkedBatches.length > 1 ? 'es' : ''}.
              All Merged batches share the same merged student and instructor data.
            </Text>
          </Alert>
        )}
      </Paper>

      {/* Comments Section */}
      <Paper
        ref={commentsRef}
        shadow="md"
        radius={isMobile ? "md" : "lg"}
        p={isMobile ? "sm" : "md"}
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,253,250,0.9) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)',
          scrollMarginTop: isMobile ? '80px' : '100px',
        }}
      >
        <SectionHeader
          icon={<IconFileText size={isMobile ? 18 : 20} />}
          title="Additional Comments"
          gradient="teal cyan"
        />

        <Textarea
          label="Comments"
          placeholder="Enter any additional comments or notes..."
          value={formData.comments || ''}
          onChange={(e) => onChange('comments', e.target.value)}
          readOnly={!editMode}
          rows={isMobile ? 3 : 4}
          styles={{
            label: { fontWeight: 600, color: '#374151', marginBottom: isMobile ? 6 : 8, fontSize: isMobile ? '0.875rem' : '1rem' },
            input: {
              borderRadius: isMobile ? 8 : 12,
              border: '2px solid #e5e7eb',
              padding: isMobile ? '10px 12px' : '12px 16px',
              fontSize: isMobile ? 13 : 14,
              '&:focus': {
                borderColor: '#14b8a6',
                boxShadow: '0 0 0 4px rgba(20, 184, 166, 0.1)',
              },
              '&:hover': {
                borderColor: '#9ca3af',
              },
            },
          }}
        />
      </Paper>

      {/* Enhanced Students Section */}
      <Paper
        ref={studentsSectionRef}
        shadow="md"
        radius={isMobile ? "md" : "lg"}
        p={isMobile ? "sm" : "md"}
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(219, 234, 254, 0.9) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)',
          scrollMarginTop: isMobile ? '80px' : '100px',
        }}
      >
        <Divider
          my={isMobile ? "sm" : "md"}
          label={
            <Group gap="xs" style={{ alignItems: 'center' }}>
              <Avatar size="xs" radius="xl" color="blue" variant="light">
                <IconUsers size={14} />
              </Avatar>
              <Text fw={600} size="sm" c="blue.7">Students</Text>
              <Badge variant="light" color="blue" size="xs">
                {formData.students?.length || 0}
              </Badge>
            </Group>
          }
          labelPosition="center"
          styles={{
            label: {
              padding: '6px 14px',
              background: 'linear-gradient(135deg, #dbeafe 0%, #f0f9ff 100%)',
              borderRadius: '20px',
              border: '1px solid #bfdbfe',
              fontSize: '0.875rem',
            }
          }}
        />

        {/* Add Create Student Button */}
        {editMode && (
          <Group justify={isMobile ? "center" : "flex-end"} mb="md">
            <Button
              leftSection={<IconPlus size={isMobile ? 16 : 18} />}
              variant="gradient"
              gradient={{ from: 'blue', to: 'cyan' }}
              size={isMobile ? "sm" : "md"}
              radius="xl"
              fullWidth={isMobile}
              onClick={() => {
                if (onOpenCreateStudentModal) {
                  onOpenCreateStudentModal();
                }
              }}
              style={{
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                transition: 'all 0.2s ease',
                maxWidth: isMobile ? '100%' : 'auto',
              }}
              className="hover:scale-105"
            >
              Create New Student
            </Button>
          </Group>
        )}

        <Stack gap={isMobile ? "md" : "lg"} mb={isMobile ? "md" : "xl"}>
          {formData.students?.map((student, index) => (
            <Paper
              key={index}
              shadow="sm"
              p={isMobile ? "md" : "lg"}
              radius="md"
              withBorder
              style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                border: '1px solid #e2e8f0',
                transition: 'all 0.2s ease',
              }}
              className="hover:shadow-md hover:border-blue-300"
            >
              <Group justify="space-between" align="center" mb="md">
                <Group gap={isMobile ? "xs" : "md"} align="center">
                  <Avatar
                    size={isMobile ? "sm" : "md"}
                    radius="xl"
                    color="blue"
                    variant="light"
                    style={{
                      background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                    }}
                  >
                    <IconUser size={isMobile ? 16 : 18} />
                  </Avatar>
                  <Box>
                    <Text fw={600} size={isMobile ? "sm" : "md"} c="blue.8">
                      Student {index + 1}
                    </Text>
                    <Text size={isMobile ? "xs" : "sm"} c="dimmed">
                      {student.name || 'Enter student details'}
                    </Text>
                  </Box>
                </Group>

                <Group gap="xs">
                  {student.paymentStatus && (
                    <Badge
                      variant="light"
                      color={
                        student.paymentStatus === 'Paid' ? 'green' :
                          student.paymentStatus === 'Partial' ? 'yellow' : 'red'
                      }
                      size={isMobile ? "xs" : "md"}
                      radius="xl"
                    >
                      {student.paymentStatus}
                    </Badge>
                  )}

                  {editMode && (
                    <Tooltip label="Remove Student" position="top">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size={isMobile ? "md" : "lg"}
                        radius="xl"
                        onClick={() => {
                          if (formData.students?.length === 1) {
                            alert("At least one student is required!");
                            return;
                          }
                          if (onRemoveStudent) {
                            onRemoveStudent(index);
                          }
                        }}
                        style={{
                          transition: 'all 0.2s ease',
                        }}
                        className="hover:bg-red-50"
                      >
                        <IconTrash size={isMobile ? 14 : 16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              </Group>

              <Box
                p={isMobile ? "sm" : "md"}
                style={{
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                }}
              >
                <StudentForm
                  student={student}
                  index={index}
                  studentsDropdown={studentsDropdown}
                  onStudentChange={(idx, field, value) => onStudentFormChange?.(idx, field, value)}
                  onEmailChange={(idx, email) => onStudentEmailChange?.(idx, email)}
                  onRemove={() => {}}
                  editMode={editMode}
                  isEditBatch={isEditBatch}
                />
              </Box>
            </Paper>
          ))}
        </Stack>

        {/* Add Student Button */}
        {editMode && (
          <Button
            leftSection={<IconPlus size={isMobile ? 16 : 18} />}
            variant="gradient"
            gradient={{ from: 'blue', to: 'cyan' }}
            size={isMobile ? "sm" : "md"}
            radius="xl"
            fullWidth={isMobile}
            onClick={() => {
              if (onAddStudent) {
                onAddStudent();
              }
            }}
            style={{
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
              transition: 'all 0.2s ease',
              maxWidth: isMobile ? '100%' : 'auto',
            }}
            className="hover:scale-105"
          >
            {isMobile ? 'Add Student' : 'Add Student'}
          </Button>
        )}
      </Paper>

      {/* Enhanced Instructors Section */}
      <Paper
        ref={instructorsSectionRef}
        shadow="md"
        radius={isMobile ? "md" : "lg"}
        p={isMobile ? "sm" : "md"}
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(243, 232, 255, 0.9) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)',
          scrollMarginTop: isMobile ? '80px' : '100px',
        }}
      >
        <Divider
          my={isMobile ? "sm" : "md"}
          label={
            <Group gap="xs" style={{ alignItems: 'center' }}>
              <Avatar size="xs" radius="xl" color="grape" variant="light">
                <IconChalkboard size={14} />
              </Avatar>
              <Text fw={600} size="sm" c="grape.7">Instructors</Text>
              <Badge variant="light" color="grape" size="xs">
                {(formData.instructor || []).length}
              </Badge>
            </Group>
          }
          labelPosition="center"
          styles={{
            label: {
              padding: '6px 14px',
              background: 'linear-gradient(135deg, #f3e8ff 0%, #faf5ff 100%)',
              borderRadius: '20px',
              border: '1px solid #d8b4fe',
              fontSize: '0.875rem',
            }
          }}
        />

        <Stack gap={isMobile ? "md" : "lg"} mb={isMobile ? "md" : "xl"}>
          {(formData.instructor || []).map((instructor, index) => (
            <Paper
              key={index}
              shadow="sm"
              p={isMobile ? "md" : "lg"}
              radius="md"
              withBorder
              style={{
                background: 'linear-gradient(135deg, #fefbff 0%, #ffffff 100%)',
                border: '1px solid #e879f9',
                transition: 'all 0.2s ease',
              }}
              className="hover:shadow-md hover:border-purple-400"
            >
              <Group justify="space-between" align="center" mb="md">
                <Group gap={isMobile ? "xs" : "md"} align="center">
                  <Avatar
                    size={isMobile ? "sm" : "md"}
                    radius="xl"
                    color="grape"
                    variant="light"
                    style={{
                      background: 'linear-gradient(135deg, #f3e8ff 0%, #e879f9 100%)',
                    }}
                  >
                    <IconChalkboard size={isMobile ? 16 : 18} />
                  </Avatar>
                  <Box>
                    <Text fw={600} size={isMobile ? "sm" : "md"} c="grape.8">
                      Instructor {index + 1}
                    </Text>
                    <Text size={isMobile ? "xs" : "sm"} c="dimmed">
                      {instructor.name || 'Enter instructor details'}
                    </Text>
                  </Box>
                </Group>

                <Group gap="xs">
                  {editMode && (
                    <Tooltip label="Remove Instructor" position="top">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size={isMobile ? "md" : "lg"}
                        radius="xl"
                        onClick={() => {
                          if (onRemoveInstructor) {
                            onRemoveInstructor(index);
                          }
                        }}
                        style={{
                          transition: 'all 0.2s ease',
                        }}
                        className="hover:bg-red-50"
                      >
                        <IconTrash size={isMobile ? 14 : 16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              </Group>

              <Box
                p={isMobile ? "sm" : "md"}
                style={{
                  background: '#fef7ff',
                  borderRadius: '8px',
                  border: '1px solid #f3e8ff',
                }}
              >
                <InstructorForm
                  key={index}
                  instructor={instructor}
                  index={index}
                  instructorsDropdown={instructorsDropdown}
                  onInstructorChange={(idx, field, value) => onInstructorFormChange?.(idx, field, value)}
                  onInstructorEmailChange={(idx, email) => onInstructorEmailChange?.(idx, email)}
                  onRemove={(idx) => onRemoveInstructor?.(idx)}
                  editMode={editMode}
                  isEditBatch={isEditBatch}
                  batchNo={formData.batch_no}
                  courseName={formData.courseName}
                  isSuperAdmin={isSuperAdmin}
                />
              </Box>
            </Paper>
          ))}
        </Stack>

        {/* Add Instructor Button */}
        {editMode && (
          <Button
            leftSection={<IconPlus size={isMobile ? 16 : 18} />}
            variant="gradient"
            gradient={{ from: 'grape', to: 'pink' }}
            size={isMobile ? "sm" : "md"}
            radius="xl"
            fullWidth={isMobile}
            onClick={() => {
              if (onAddInstructor) {
                onAddInstructor();
              }
            }}
            style={{
              boxShadow: '0 4px 12px rgba(168, 85, 247, 0.25)',
              transition: 'all 0.2s ease',
              maxWidth: isMobile ? '100%' : 'auto',
            }}
            className="hover:scale-105"
          >
            {isMobile ? 'Add Instructor' : 'Add Instructor'}
          </Button>
        )}
      </Paper>
    </Stack>
  );
};

export { BatchForm };